// FILE: hooks/useEntitySearch.ts
"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export type SearchParams = Record<string, string | number | undefined | null>;

/** Key ổn định để cache + so sánh: sort key nên thứ tự param không ảnh hưởng */
function buildQS(query: string, params: SearchParams): string {
  const sp = new URLSearchParams();
  if (query) sp.set("q", query);
  for (const k of Object.keys(params).sort()) {
    const v = params[k];
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  return sp.toString();
}

export interface UseEntitySearchOptions<T> {
  endpoint: string;
  initialData?: T[];
  initialQuery?: string;
  initialParams?: SearchParams;
  /** Dưới ngưỡng này coi như KHÔNG tìm (trả full list), tránh query %a% vô nghĩa */
  minChars?: number;
  debounceMs?: number;
  cacheSize?: number;
  /** Rút mảng từ JSON response. Default: res.data */
  select?: (json: any) => T[];
}

export function useEntitySearch<T = any>({
  endpoint,
  initialData = [],
  initialQuery = "",
  initialParams = {},
  minChars = 2,
  debounceMs = 280,
  cacheSize = 50,
  select = (j) => j?.data ?? [],
}: UseEntitySearchOptions<T>) {
  const [query, setQuery] = useState(initialQuery);
  const [params, setParams] = useState<SearchParams>(initialParams);
  const [data, setData] = useState<T[]>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);
  const cacheRef = useRef<Map<string, T[]>>(new Map());
  const prevQueryRef = useRef(initialQuery);

  // Dữ liệu SSR đã có sẵn cho tổ hợp ban đầu → seed cache, khỏi fetch lại lúc mount
  const initialQSRef = useRef(buildQS(initialQuery, initialParams));
  const seededRef = useRef(false);
  if (!seededRef.current) {
    cacheRef.current.set(initialQSRef.current, initialData);
    seededRef.current = true;
  }

  const effectiveQuery = query.trim().length >= minChars ? query.trim() : "";
  const qs = buildQS(effectiveQuery, params);

  const run = useCallback(
    (targetQS: string) => {
      const cached = cacheRef.current.get(targetQS);
      if (cached) {
        setData(cached);
        setLoading(false);
        setError(null);
        return;
      }

      // Hủy request cũ: chống response về muộn đè kết quả mới
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const myId = ++reqIdRef.current;

      setLoading(true);
      setError(null);

      fetch(`${endpoint}${targetQS ? `?${targetQS}` : ""}`, { signal: ctrl.signal })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((json) => {
          if (myId !== reqIdRef.current) return; // response cũ → vứt
          const arr = select(json);
          cacheRef.current.set(targetQS, arr);
          if (cacheRef.current.size > cacheSize) {
            const oldest = cacheRef.current.keys().next().value;
            if (oldest !== undefined) cacheRef.current.delete(oldest);
          }
          setData(arr);
          setLoading(false);
        })
        .catch((err: any) => {
          if (err?.name === "AbortError") return; // KHÔNG tắt loading: request mới đang chạy
          if (myId !== reqIdRef.current) return;
          setError(err?.message ?? "Lỗi tải dữ liệu");
          setLoading(false);
        });
    },
    [endpoint, cacheSize, select],
  );

  useEffect(() => {
    const queryChanged = prevQueryRef.current !== effectiveQuery;
    prevQueryRef.current = effectiveQuery;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Gõ chữ → debounce. Đổi filter/sort (click) → chạy ngay.
    const delay = queryChanged ? debounceMs : 0;
    debounceRef.current = setTimeout(() => run(qs), delay);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs, effectiveQuery, debounceMs, run]);

  // Dọn khi unmount: hủy request đang bay, tránh setState sau khi tháo component
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const setParam = useCallback((key: string, value: string | number | null) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  }, []);

  return { data, loading, error, query, setQuery, params, setParams, setParam, qs };
}